import TaskDialog from '@jimmyboys/liita-ui/dist/components/TaskDialog'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import toMS from '@jimmyboys/liita-ui/dist/utils/toMS'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, usePrevious } from 'react-use'
import { useRecoilState } from 'recoil'
import { useTaskManager, useReload } from 'store'
import { openedTask } from '../../store'
import * as dialogs from './controllers/index'

import themeStyles from 'assets/styles/theme.module.sass'
import { matchPath, MemoryRouter, Route, Switch } from 'react-router-dom'
import PostponeTaskController from './controllers/PostponeTaskController/PostponeTaskController'
import RefuseTaskController from './controllers/RefuseTaskController/RefuseTaskController'
import RefuseRequestTaskController from './controllers/Requests/RefuseRequestTaskController/RefuseRequestTaskController'
import ForwardTaskController from './controllers/ForwardTaskController/ForwardTaskController'

import styles from './TaskManager.module.sass'

const { transitionTime, tabBarHeight } = themeStyles

const TaskManager = () => {
	const [currentOpenedTask, setCurrentOpenedTask] = useRecoilState(openedTask)
	const { openTask, closeTask, openNextTask } = useTaskManager()
	const reload = useReload()
	const history = useHistory()

	const location = useLocation()
	const [queryParams, , setSearchParams] = useQueryParams(location)
	const [closing, setClosing] = useState(null)
	const { screenSize } = useGlobal()

	const { taskId } = queryParams
	const prevTaskId = usePrevious(taskId)

	const activeTaskRef = useRef()

	useEffect(() => {
		if (!currentOpenedTask?.openTask) {
			activeTaskRef.current = taskId
			return
		}

		if (prevTaskId && taskId !== activeTaskRef.current) {
			setClosing({ taskId: activeTaskRef.current })
			activeTaskRef.current = taskId
		}
	}, [openTask, taskId, currentOpenedTask?.openTask, prevTaskId])

	useEffect(() => {
		if (!closing?.taskId) return

		let timeout

		timeout = setTimeout(() => {
			if (
				closing.taskId === activeTaskRef.current ||
				history.location.hash.startsWith('#C_')
			) {
				history.push(
					`/clinical-processes/${currentOpenedTask?.openTask?.processId}/appointments`
				)
			}
			closeTask(!closing.openNext)
			setClosing({ taskId: null })
			activeTaskRef.current = null

			if (closing.openNext) {
				openNextTask(currentOpenedTask?.openTask?.processId)
			}
		}, toMS(transitionTime))

		return () => {
			clearTimeout(timeout)
		}
		// eslint-disable-next-line
	}, [closeTask, closing, openNextTask, setSearchParams, taskId])

	useEffect(() => {
		if (closing?.taskId) return

		if (taskId) {
			openTask(taskId)
		}
	}, [closing, openTask, taskId])

	useEffect(() => {
		const match = matchPath(location?.pathname, {
			path: '/clinical-processes/:clinicalProcessId',
			strict: false,
			exact: false,
		})

		if (
			currentOpenedTask?.openTask?.processId &&
			currentOpenedTask.openTask.processId !==
				match?.params?.clinicalProcessId
		) {
			setClosing({ taskId: taskId || currentOpenedTask.openTask.id })
		}
	}, [
		currentOpenedTask?.openTask?.id,
		currentOpenedTask?.openTask?.processId,
		location?.pathname,
		taskId,
	])

	const finalizeTask = useCallback(
		(success, openNext) => {
			setClosing({
				taskId: taskId || currentOpenedTask?.openTask?.id,
				openNext,
			})

			if (success) {
				reload([
					'/tasks/me?state=Created',
					'/tasks/me?state=Postponed',
					'/tasks/stats',
					`/clinical_processes/${currentOpenedTask?.openTask?.processId}/tasks`,
					`clinical_processes/${currentOpenedTask?.openTask?.processId}`
				])
			}
		},
		[
			currentOpenedTask?.openTask?.id,
			currentOpenedTask?.openTask?.processId,
			reload,
			taskId,
		]
	)

	const View = dialogs[currentOpenedTask?.openTask?.type]

	return currentOpenedTask?.openTask ? (
		<TaskDialog
			className={styles['task-dialog']}
			dialogId={'task-manager'}
			title={
				`#${
					currentOpenedTask?.openTask?.requisitionId ||
					currentOpenedTask?.openTask?.processId
				}` || ''
			}
			$closing={!!closing?.taskId}
			dockable={true}
			scrollable={true}
			icon={currentOpenedTask?.openTask?.icon || ''}
			startingPoint='right'
			offsetBottom={
				screenSize === 'small'
					? `${parseInt(tabBarHeight, 10) + 15}px`
					: '20px'
			}
			setDialogDetails={setCurrentOpenedTask}
		>
			<MemoryRouter dismiss={finalizeTask}>
				<Switch>
					<Route path='/' exact={true}>
						{View && (
							<View
								id={currentOpenedTask?.openTask?.id}
								data={currentOpenedTask?.openTask}
								dismiss={finalizeTask}
								resolve={() => {}}
								reject={() => {}}
							/>
						)}
					</Route>
					<Route path='/refuse' exact={true}>
						<RefuseTaskController
							id={currentOpenedTask?.openTask?.id}
							data={currentOpenedTask?.openTask}
							dismiss={finalizeTask}
							resolve={() => {}}
							reject={() => {}}
						/>
					</Route>
					<Route path='/refuse_request' exact={true}>
						<RefuseRequestTaskController
							id={currentOpenedTask?.openTask?.id}
							data={currentOpenedTask?.openTask}
							dismiss={finalizeTask}
							resolve={() => {}}
							reject={() => {}}
						/>
					</Route>
					<Route path='/postpone' exact={true}>
						<PostponeTaskController
							id={currentOpenedTask?.openTask?.id}
							data={currentOpenedTask?.openTask}
							dismiss={finalizeTask}
							resolve={() => {}}
							reject={() => {}}
						/>
					</Route>
					<Route path='/forward' exact={true}>
						<ForwardTaskController
							id={currentOpenedTask?.openTask?.id}
							data={currentOpenedTask?.openTask}
							dismiss={finalizeTask}
							resolve={() => {}}
							reject={() => {}}
						/>
					</Route>
					<Route path='/success' exact={true}>
						<ForwardTaskController
							id={currentOpenedTask?.openTask?.id}
							data={currentOpenedTask?.openTask}
							dismiss={finalizeTask}
							resolve={() => {}}
							reject={() => {}}
						/>
					</Route>
					<Route>
						<div>Action not available</div>
					</Route>
				</Switch>
			</MemoryRouter>
		</TaskDialog>
	) : null
}

export default TaskManager
